/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React, { useMemo } from 'react';

import { jsx } from '@compiled/react';

import { useAINudgesRefreshed } from '@atlassian/ai-smart-button-api';
import { type RovoExperienceSidebarAPI } from '@atlassian/rovo-experience-api';

import { useNudgeSessionState } from './use-nudge-session-state';

// Lazy load to avoid loading useRovoActionTrigger when there's no active nudge
const LazyAIProactiveNudgeRefreshed = React.lazy(() =>
	import(/* webpackChunkName: "@atlaskit-internal_ai-proactive-nudge-refreshed" */ './main').then(
		(module) => ({
			default: module.AIProactiveNudgeRefreshed,
		}),
	),
);

type AIProactiveNudgeProps = {
	product?: string;
	setShowButtonAnimation?: (showState: boolean) => void;
	sidebarApi: RovoExperienceSidebarAPI;
};

export const AIProactiveNudgeRefreshed = ({
	sidebarApi,
	product,
	setShowButtonAnimation,
}: AIProactiveNudgeProps) => {
	const { acceptedNudges, dismissedNudges, addAcceptedNudge, addDismissedNudge } =
		useNudgeSessionState();
	const selectedNudges = useAINudgesRefreshed();

	const potentialActiveNudges = useMemo(() => {
		/**
		 * ConversationAction must not be previously dismissed or accepted
		 * to be considered active.
		 */
		return selectedNudges?.filter(
			(nudge) => !dismissedNudges.includes(nudge.id) && !acceptedNudges.includes(nudge.id),
		);
	}, [selectedNudges, dismissedNudges, acceptedNudges]);

	// Only render the lazy component if there's a potential nudge to show
	if (!potentialActiveNudges?.length) {
		return null;
	}

	return (
		<React.Suspense fallback={null}>
			<LazyAIProactiveNudgeRefreshed
				sidebarApi={sidebarApi}
				potentialActiveNudges={potentialActiveNudges}
				addAcceptedNudge={addAcceptedNudge}
				addDismissedNudge={addDismissedNudge}
				product={product}
				setShowButtonAnimation={setShowButtonAnimation}
			/>
		</React.Suspense>
	);
};
